import styled from 'styled-components'
import { Gradient, GradientOverlayColor } from '../../../constants/contentfulStyle.constant'
import { setPaddingSize } from '../../../utils/contentfulStyles.utils'

export const ResourceItem = styled.div<{
  $hasOverlay: boolean
  $videoIcon: string
  $companyLogo: string
  $categoryIcon: string
  $imageOverlay: string
}>`
  > a {
    text-decoration: none;
  }

  ${({ theme, $hasOverlay, $videoIcon, $companyLogo, $categoryIcon }) =>
    $hasOverlay
      ? `
    .ResourceRow__Image-Container:before {
      content: '';
      background: rgb(23, 33, 37, ${$companyLogo || $categoryIcon || $videoIcon ? '.1' : '.05'});
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      position: absolute;
    }`
      : ''}

  ${({ theme, $companyLogo, $categoryIcon, $videoIcon, $hasOverlay }) => {
    if ($companyLogo)
      return `
      .ResourceRow__Item-Logo {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        height: 120px;
        width: 240px;

        ${theme.breakpoints.down('md')} {
          height: 95px;
          width: 190px;
        }

        ${theme.breakpoints.down('sm')} {
          height: 113px;
          width: 226px;
        }

        ${theme.breakpoints.down('xs')} {
          height: 103px;
          width: 206px;
        }
      }
    `

    let iconStyles = ''
    if ($categoryIcon || $videoIcon) {
      iconStyles += `
        position: absolute;
        left: 1px;
        bottom: 1px;
        height: 48px;
        width: 48px;
      `
    } else {
      return ``
    }

    if ($videoIcon) {
      iconStyles = `
        .ResourceRow__Item-Icon {
          ${$hasOverlay ? `opacity: .8;` : ''}
          ${iconStyles}
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
      `
    } else if ($categoryIcon) {
      iconStyles = `
        .ResourceRow__Item-Icon {
          ${iconStyles}
        }
      `
    }

    return `
      ${iconStyles}
    `
  }}
  
  .ResourceRow__Item-Video {
    div[data-cy='VideoPlayer-Image']:after {
      background: ${({ theme }) => theme.webMktNewPalette.alphaBlack[20]};
    }
  }

  &:hover {
    box-shadow: 0px 16px 16px -8px ${({ theme }) => theme.webMktNewPalette.alphaBlack[10]};
    .ResourceRow__Item-Video {
      div[data-cy='VideoPlayer-Image']:after {
        background: ${({ theme }) => theme.webMktNewPalette.alphaBlack[40]};
      }
    }
  }

  ${({ $imageOverlay, theme }) => {
    if ($imageOverlay === Gradient.TEAL) {
      let gradient = GradientOverlayColor(Gradient.TEAL)

      return `
        .ResourceRow__Item-Video {
          div[data-cy='VideoPlayer-Image']:after {
            background: ${gradient};
          }
        }
        &:hover {
          .ResourceRow__Item-Video {
            div[data-cy='VideoPlayer-Image']:after {
              background: ${gradient};
              opacity: 0.5;
            }
          }
        }
      `
    }
  }}

  width: 384px;
  background: ${({ theme }) => theme.webMktNewPalette.white};
  cursor: pointer;

  .CTA {
    margin: 16px 0 0;
    position: relative;
    z-index: 1;
  }

  &:not(:last-of-type) {
    margin-right: 24px;
  }

  &.Overlay:before {
    content: '';
    background: ${({ theme }) => theme.webMktNewPalette.alphaBlack[10]};
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    position: absolute;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 304px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 360px;
    &:nth-of-type(1),
    &:nth-of-type(3) {
      margin-right: 16px;
    }
    &:nth-of-type(2) {
      margin: 0;
    }
    &:nth-of-type(3),
    &:nth-of-type(4) {
      margin-top: 16px;
    }
  }

  ${({ theme }) => theme.breakpoints.down('xs')} {
    max-width: 329px;
    width: 100%;

    &:nth-of-type(1) {
      margin: 0;
    }

    &:nth-of-type(2),
    &:nth-of-type(3),
    &:nth-of-type(4) {
      margin-top: 16px;
    }
  }
`

export const ResourceRowContainer = styled.div<{
  $topPadding: string
  $bottomPadding: string
  $pageType: string
  $isHomePage: boolean
  $theme: string
}>`
  ${({ theme, $topPadding, $bottomPadding, $theme, $pageType, $isHomePage }) => {
    let cardBoxAlignment = $pageType === 'aboutRPA' ? 'margin: 0 auto 0 0' : 'margin: 0 auto'
    let seeAllCtaColor =
      $theme === 'Grey'
        ? `color: ${theme.webMktNewPalette.action.light.primary}`
        : `color: ${theme.webMktNewPalette.white}`
    return `
      padding-top: ${setPaddingSize($topPadding)}px;
      padding-bottom: ${setPaddingSize($bottomPadding)}px;
      background-color: ${
        $theme === 'Grey'
          ? theme.webMktNewPalette.deepBlue[10]
          : theme.webMktNewPalette.deepBlue[70]
      };
      ${
        $isHomePage
          ? `
        ${theme.breakpoints.up('md')} {
          padding-top: 1px; // applied for negative margins
        }

        .ResourceRow__Outer-Container {
          margin-top: -81px;

          ${theme.breakpoints.down('md')} {
            margin-top: -69px;
          }

          ${theme.breakpoints.down('sm')} {
            margin-top: 0;
          }
        }
      `
          : ``
      }

      &.resourceRow--fullWidth {
        width: 100%;
        .ResourceRow__Outer-Container {
          ${cardBoxAlignment}
        }
      }


      &.resourceRow--fourCards {
        .ResourceRow__CTA-Container {
          .CTA {
            ${seeAllCtaColor}
          }
        }
      }

    `
  }}
  &.resourceRow--slider {
    background-color: ${({ theme, $theme }) =>
     $theme === 'Grey'
      ? theme.webMktNewPalette.deepBlue[10]
      : $theme === 'Dark'
      ? theme.webMktNewPalette.deepBlue[70]
      : theme.webMktNewPalette.background.light.default
    };
    .slider__usecase {
      margin-left: 182px;
      position: relative;
      max-width: 1740px;
      @media (min-width: 1921px) {
        margin: 0 auto;
      }
      ${({ theme }) => theme.breakpoints.down('lg')} {
        margin-left: 150px;
      }
      ${({ theme }) => theme.breakpoints.down('xs')} {
        margin-left: 76px;
      }

      @media (max-width: 767px) {
        margin-left: 46px;
      }

      .slick-slider {
        position: static;
        .slick-arrow {
          top: 15px;
          z-index: 9;
        }
        .slick-prev {
          right: 13%;
          left: unset;
          background-image: url(https://images.ctfassets.net/5965pury2lcm/3TDnHpzrntD3MbiqGjdFdZ/2fbcf13a94f38716346f3da40b17a648/image-left.png);
          background-image: 
            ${({ theme, $theme }) =>
            $theme === 'Grey'
              ? 'url(https://images.ctfassets.net/5965pury2lcm/6mpIEc9t6UXDSrVgeY8ICs/9190ee248421d42177bd97245cf0d100/image-left__1_.png)'
              : $theme === 'Dark'
              ? 'url(https://images.ctfassets.net/5965pury2lcm/3TDnHpzrntD3MbiqGjdFdZ/2fbcf13a94f38716346f3da40b17a648/image-left.png)'
              : 'url(https://images.ctfassets.net/5965pury2lcm/6mpIEc9t6UXDSrVgeY8ICs/9190ee248421d42177bd97245cf0d100/image-left__1_.png)'
          };
          background-size: contain;
          background-repeat: no-repeat;
          width: 23px;
          height: 21px;
          position: absolute;
          ${({ theme }) => theme.breakpoints.down('lg')} {
            right: 14%;
          }
          ${({ theme }) => theme.breakpoints.down('md')} {
            right: 15%;
          }
          ${({ theme }) => theme.breakpoints.down('sm')} {
            right: 17%;
          }
          ${({ theme }) => theme.breakpoints.down('xs')} {
            right: 20%;
          }
        }
        .slick-next {
          position: absolute;

          right: 10%;
          left: unset;
          background-image: 
            ${({ theme, $theme }) =>
            $theme === 'Grey'
              ? 'url(https://images.ctfassets.net/5965pury2lcm/74gRK9duM9m9yOEHzgvwQs/cf7d486997c053cf42c2464d266ed697/arrow-right__2_.svg)'
              : $theme === 'Dark'
              ? 'url(https://images.ctfassets.net/5965pury2lcm/6DmsHdAKdmddDjGO3qfA3n/6dce48cf6faeffd2e20d393dcdcbe228/arrow-right.svg)'
              : 'url(https://images.ctfassets.net/5965pury2lcm/74gRK9duM9m9yOEHzgvwQs/cf7d486997c053cf42c2464d266ed697/arrow-right__2_.svg)'
          };
          background-size: contain;
          background-repeat: no-repeat;
          width: 23px;
          height: 21px;
        }
        .slick-prev:before {
          content: '';
        }
        .slick-next:before {
          content: '';
        }
        .slick-prev.slick-disabled {
          background-image: url(https://images.ctfassets.net/5965pury2lcm/62lPGucATQ1rKpayU6SNHi/e81fc386aa45220f8341f6ee67e14547/arrow-left.svg);

          cursor: default;
        }
        .slick-next.slick-disabled {
          background-image: url(https://images.ctfassets.net/5965pury2lcm/2dWc7y6xQMUB5Sko2oTOfx/8ff8962fd688e1e7c9e58491a945375e/arrow-right-disabled.png);

          cursor: default;
        }
      }

      .slider-for {
        .slick-slide {
          display: flex !important;
          height: auto !important;

          > div > div {
            height: 100%;
          }
        }

        // Remove margin on last visible item by adjusting slick-track's margin
        .slick-track {
          display: flex !important;
          margin: 0;
        }
      }
      .ResourceRow__SeeAllItemsIntro {
        text-align: left !important;
        margin: 0 0 60px !important;
        color: ${({ theme, $theme }) =>
        $theme === 'Grey'
          ? theme.webMktNewPalette.deepBlue[70]
          : $theme === 'Dark'
          ? theme.webMktNewPalette.deepBlue[30]
          : theme.webMktNewPalette.deepBlue[60]};
      }
      .ResourceRow__Item {
        border: 3px solid
          ${({ theme, $theme }) =>
            $theme === 'Grey'
              ? theme.webMktNewPalette.deepBlue[10]
              : $theme === 'Dark'
              ? theme.webMktNewPalette.deepBlue[80]
              : theme.webMktNewPalette.background.light.accent};
          background-color: ${({ theme, $theme }) =>
          $theme === 'Grey'
            ? theme.webMktNewPalette.deepBlue[10]
            : $theme === 'Dark'
            ? theme.webMktNewPalette.deepBlue[80]
            : theme.webMktNewPalette.background.light.accent};
        padding: 16px 16px 48px;
        transition: 0.3s;
        position: relative;
        width: auto;
        margin-right: 32px;
        height: calc(100% - 70px);

        ${({ theme }) => theme.breakpoints.down('lg')} {
          margin-right: 26px;
        }

        ${({ theme }) => theme.breakpoints.down('sm')} {
          margin-right: 16px;
        }

        ${({ theme }) => theme.breakpoints.down('xs')} {
          margin: 0 auto;
          margin-right: 16px;
          max-width: unset;
        }
        .item__link {
          z-index: 99;
          position: absolute;
          width: calc(100% - 32px);
          height: calc(100% - 32px);
        }

        .ResourceRow__Image-Container {
          height: auto;

          .ResourceRow__BackgroundImg {
            height: 159px;
            object-fit: cover;

            @media (max-width: 1919px) {
              height: 200px;
            }

            ${({ theme }) => theme.breakpoints.down('lg')} {
              height: 140px;
            }

            ${({ theme }) => theme.breakpoints.down('sm')} {
              height: 200px;
            }

            ${({ theme }) => theme.breakpoints.down('xs')} {
              height: 110px;
            }

            @media (max-width: 767px) {
              height: 200px;
            }

            @media (max-width: 567px) {
              height: 105px;
            }
          }
        }
        .ResourceRow__Text-Container {
          padding: 0;
          margin-bottom: 0;
          margin-top: 24px;
          min-height: 211px;
          position: unset;
          ${({ theme }) => theme.breakpoints.down('xs')} {
            min-height: 229px;
          }
          .ResourceRow__Meta-Container {
            color: ${({ theme }) => theme.webMktNewPalette.deepBlue[30]};
          }
          .ResourceRow__Item-Heading {
            color: ${({ theme, $theme }) =>
            $theme === 'Grey'
              ? theme.webMktNewPalette.deepBlue[70]
              : $theme === 'Dark'
              ? theme.webMktNewPalette.white
              : theme.webMktNewPalette.secondary};
            font-size: 20px;
            line-height: 24px;
            margin-top: 24px;

            ${({ theme }) => theme.breakpoints.down('xs')} {
              font-size: 18px;
            }
          }
          .CTA {
            position: absolute;
            bottom: 16px;
            span {
              display: none;
            }
            .CTA__Arrow-Container {
              margin: 0;
              ::before {
                color: ${({ theme }) => theme.webMktNewPalette.deepBlue[55]};
              }
            }
            svg {
              fill: ${({ theme }) => theme.webMktNewPalette.deepBlue[55]};
            }
          }
        }
        :hover {
          background-color: ${({ theme, $theme }) =>
          $theme === 'Grey'
            ? theme.webMktNewPalette.deepBlue[20]
            : $theme === 'Dark'
            ? theme.webMktNewPalette.deepBlue[90]
            : theme.webMktNewPalette.deepBlue[10] };
          .ResourceRow__Text-Container {
            .ResourceRow__Item-Heading {
              color: ${({ theme, $theme }) =>
              $theme === 'Grey'
                ? theme.webMktNewPalette.secondary
                : $theme === 'Dark'
                ? theme.webMktNewPalette.background.light.default
                : theme.webMktNewPalette.secondary};
            }
          }
          .CTA {
            .CTA__Arrow-Container {
              ::before {
                color: var(--orange-50);
              }
            }
            svg {
              fill: ${({ theme }) => theme.webMktNewPalette.orange[50]};
            }
          }
        }
      }
    }
  }
  &.resourceRow--fullWidth {
    width: 100%;
    .ResourceRow__CTA-Container {
      text-align: left;
      ${({ theme }) => theme.breakpoints.down('xs')} {
        text-align: center;
      }
    }
    .ResourceRow__Card-Container {
      padding-bottom: 10px;
      justify-content: flex-start;
      .ResourceRow__Item {
        position: relative;

        .ResourceRow__Item-Video {
          .VideoPlayer__Image-File {
            height: 312px;
            object-fit: cover;
          }
          &.noMediaContainer {
            display: none;
          }
        }
        .TextContainer_Video_Overlay {
          position: absolute;
          inset: 0px;
          z-index: 1;
          opacity: 0;
          overflow: hidden;
          z-index: 9;
          > div,
          div > div {
            height: 100%;
          }
        }

        .ResourceRow__Image-Container {
          height: 312px;
          .ResourceRow__Pill-Container {
            display: none;
          }
          .ResourceRow__CtaBox {
            position: absolute;
            bottom: 0px;
            padding: 24px;
            .CTA {
              padding: 12px 12px;
              margin: 0;
              height: 40px;
              line-height: normal;
              .CTA__Arrow-Container {
                margin-left: 0;
                width: 16px;
                height: 16px;
                svg {
                  width: 16px;
                  height: 16px;
                }
              }
            }
          }
        }
        .ResourceRow__Text-Container {
          margin: 24px 0;
          .CTA {
            display: none;
          }
          .ResourceRow__Meta-Container {
            p {
            }
          }
          .ResourceRow__Item-Heading {
            margin: 32px 0;
          }
          .ResourceRow__Item-Body {
            margin: 0 0 24px 0;
            p {
            }
          }
        }

        &.tcVideo {
          .ResourceRow__Text-Container {
            .CTA {
              display: none;
            }
          }
        }

        .noMediaContainer {
          min-height: 224px;
          position: static;
          padding-bottom: 40px;
          .CTA {
            display: block;
            margin-bottom: 16px;
          }
          .noMedia {
            width: 40px;
            position: absolute;
            bottom: 24px;
            .CTA {
              padding: 12px 12px;
              margin: 0;
              height: 40px;
              line-height: normal;
              .CTA__Arrow-Container {
                margin-left: 0;
                width: 16px;
                height: 16px;
                svg {
                  width: 16px;
                  height: 16px;
                }
              }
            }
          }
        }
      }
    }
  }

  &.resourceRow--fourCards {
    width: 100%;
    .ResourceRow__Outer-Container {
      max-width: 1608px;
      ${({ theme }) => theme.breakpoints.down('sm')} {
        max-width: 736px;
      }
    }
    .ResourceRow__CTA-Container {
      text-align: left;
      ${({ theme }) => theme.breakpoints.down('xs')} {
        text-align: center;
      }
    }
    .ResourceRow__Card-Container {
      padding-bottom: 10px;
      .ResourceRow__Item {
        position: relative;

        ${({ theme }) => theme.breakpoints.down('xs')} {
          margin-right: 0;
        }

        .ResourceRow__Item-Video {
          .VideoPlayer__Image-File {
            height: 216px;
            object-fit: cover;
          }
          &.noMediaContainer {
            display: none;
          }
        }
        .TextContainer_Video_Overlay {
          position: absolute;
          inset: 0px;
          z-index: 1;
          opacity: 0;
          overflow: hidden;
          z-index: 9;
          > div,
          div > div {
            height: 100%;
          }
        }

        .ResourceRow__Image-Container {
          height: 216px;
          .ResourceRow__Pill-Container {
            display: none;
          }
          .ResourceRow__CtaBox {
            position: absolute;
            bottom: 0px;
            padding: 24px;
            .CTA {
              padding: 12px 12px;
              margin: 0;
              height: 40px;
              line-height: normal;
              .CTA__Arrow-Container {
                margin-left: 0;
                width: 16px;
                height: 16px;
                svg {
                  width: 16px;
                  height: 16px;
                }
              }
            }
          }
        }
        .ResourceRow__Text-Container {
          margin: 24px 0;
          height: calc(100% - 264px);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          .CTA {
            display: inline;
            color: ${({ theme }) => theme.webMktNewPalette.black};
          }
          .ResourceRow__Meta-Container {
            p {
            }
          }
          .ResourceRow__Item-Heading {
            font-size: 20px;
            line-height: 24px;
            margin: 16px 0;
          }
          .ResourceRow__Item-Body {
            margin: 0 0 16px 0;
            p {
              font-size: 14px;
              line-height: 24px;
            }
          }
        }

        &.tcVideo {
          .ResourceRow__Text-Container {
            .CTA {
              display: none;
            }
          }
        }

        .noMediaContainer {
          min-height: 224px;
          position: static;
          height: calc(100% - 48px);
          .CTA {
            display: block;
            margin-bottom: 16px;
            span {
              display: none;
            }
            .CTA__Arrow-Container {
              margin-left: 0px;
            }
          }
          .noMedia {
            width: 40px;
            position: absolute;
            bottom: 24px;
            .CTA {
              padding: 12px 12px;
              margin: 0;
              height: 40px;
              line-height: normal;
              .CTA__Arrow-Container {
                margin-left: 0;
                width: 16px;
                height: 16px;
                svg {
                  width: 16px;
                  height: 16px;
                }
              }
            }
          }
        }
      }
    }
  }

  .ResourceRow__Card-Container {
    display: flex;
    justify-content: center;

    ${({ theme }) => theme.breakpoints.down('sm')} {
      flex-wrap: wrap;
      margin: 0 auto;
      justify-content: start;
    }

    ${({ theme }) => theme.breakpoints.down('xs')} {
      max-width: 328px;
    }

    .ResourceRow__Item {
      position: relative;
      > a {
        height: 100%;
        display: block;
        position: absolute;
        width: 100%;
        z-index: 1;
      }

      .ResourceRow__Item-Video {
        .VideoPlayer__Image-File {
          height: 216px;
          object-fit: cover;
          ${({ theme }) => theme.breakpoints.down('md')} {
            height: 171px;
          }

          ${({ theme }) => theme.breakpoints.down('sm')} {
            height: 203px;
          }

          ${({ theme }) => theme.breakpoints.down('xs')} {
            height: 185px;
          }
        }
        &.noMediaContainer {
          display: none;
        }
      }
      .TextContainer_Video_Overlay {
        position: absolute;
        inset: 0px;
        z-index: 1;
        opacity: 0;
        overflow: hidden;
        z-index: 9;
        > div,
        div > div {
          height: 100%;
        }
      }

      &.tcVideo {
        .ResourceRow__Text-Container {
          .CTA {
            display: none;
          }
        }
      }
    }
  }

  .ResourceRow__Pill-Container {
    display: flex;
    position: absolute;
    top: 0;
    left: 0;
  }

  .ResourceRow__Pill {
    padding: 4px 8px;
    margin: 0;
    background: ${({ theme }) => theme.webMktNewPalette.white};
    color: ${({ theme }) => theme.webMktNewPalette.deepBlue[55]};
    margin-right: 1px;
  }

  .ResourceRow__Image-Container {
    width: 100%;
    height: 216px;
    position: relative;

    ${({ theme }) => theme.breakpoints.down('md')} {
      height: 171px;
    }

    ${({ theme }) => theme.breakpoints.down('sm')} {
      height: 203px;
    }

    ${({ theme }) => theme.breakpoints.down('xs')} {
      height: 185px;
      overflow: hidden;
    }

    img {
      object-fit: cover;
      object-position: center;
    }

    .ResourceRow__BackgroundImg {
      width: 100%;
      height: 100%;
    }
  }

  .ResourceRow__Text-Container {
    padding: 0 24px;
    position: relative;
    margin: 32px 0;

    > *:first-child {
      margin-top: 0;
    }

    ul,
    ol {
      list-style: disc;
      padding-left: 24px;
      li {
        margin-top: 8px;
        p {
          margin: 0;
        }
      }
    }

    ${({ theme }) => theme.breakpoints.down('md')} {
      margin: 24px 0;
    }
  }

  .ResourceRow__Item-Date {
    color: ${({ theme }) => theme.webMktNewPalette.alphaBlack[30]};
    margin-left: auto;
  }

  .ResourceRow__Meta-Container {
    display: flex;
    align-items: center;
  }

  .ResourceRow__Item-Category {
    color: ${({ theme }) => theme.webMktNewPalette.deepBlue[55]};
    margin: 0;
  }

  .ResourceRow__Item-Heading,
  .ResourceRow__Item-Body {
    margin-top: 16px;
  }

  .ResourceRow__Item-Heading:hover {
    color: ${({ theme }) => theme.webMktNewPalette.action.light.primary};
  }

  .ResourceRow__Item-Body {
    p,
    li {
      color: ${({ theme }) => theme.webMktNewPalette.deepBlue[55]};
    }
  }

  .ResourceRow__SeeAllItemsIntro {
    margin-top: 48px;
    text-align: center;
    margin: 48px auto 0;
    max-width: 701px;

    ${({ theme }) => theme.breakpoints.down('sm')} {
      max-width: 548px;
    }

    ${({ theme }) => theme.breakpoints.down('xs')} {
      max-width: 329px;
    }
  }

  .ResourceRow__CTA-Container {
    text-align: center;
    margin-top: 24px;
  }
`
